import { inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { fromEvent, merge, timer, Subject, Subscription, interval } from 'rxjs';
import { debounceTime, filter, take, takeUntil, tap, map } from 'rxjs/operators';


import { PhNotificationComponent } from '@ph-shared/components/notification/notification/notification.component';
import { PhNotificationType } from '@ph-shared/components/notification/notification/notification.interface';

import { ConfigService } from '../config/config.service';
import { PhDialog } from '../dialog';
import { PhOverlayRef } from '../overlay';

interface InternalConfig {
  sessionTimeMs: number;
  warningTimeMs: number;
}

const ACTIVITY_DEBOUNCE_MS = 300;

@Injectable({ providedIn: 'root' })
export class InactivityTimeoutService implements OnDestroy {
  private readonly config: InternalConfig;
  private readonly destroy$ = new Subject<void>();
  private readonly activityDetected$ = new Subject<void>();

  private warningShown: WritableSignal<boolean> = signal(false);
  private warningMessage: WritableSignal<string> = signal('');
  private sessionStartTime: number = 0;
  private documentHidden: WritableSignal<boolean> = signal(false);
  private notificationRef: PhOverlayRef<PhNotificationComponent> | null = null;
  private sessionTimer$: Subscription | null = null;
  private warningTimer$: Subscription | null = null;
  private activitySubscription$: Subscription | null = null;
  private visibilitySubscription$: Subscription | null = null;
  private onSessionExpiry: (() => void) | null = null;

  private configService = inject(ConfigService);
  private dialog = inject(PhDialog);
  private translate = inject(TranslateService);

  constructor() {
    const env = this.configService.environment;
    const inactivity = env.features.inactivity;
    
    this.config = {
      sessionTimeMs: inactivity.sessionTimeMinutes * 60 * 1000,
      warningTimeMs: inactivity.warningTimeMinutes * 60 * 1000,
    };
  }

  startMonitoring(onExpiry: () => void): void {
    if (this.config.sessionTimeMs <= 0) {
      return;
    }

    this.stopMonitoring();
    this.onSessionExpiry = onExpiry;
    this.setupActivityDetection();
    this.setupPageVisibilityMonitoring();
    this.resetSessionTimer();
  }

  stopMonitoring(): void {
    this.sessionTimer$?.unsubscribe();
    this.sessionTimer$ = null;

    this.warningTimer$?.unsubscribe();
    this.warningTimer$ = null;

    this.activitySubscription$?.unsubscribe();
    this.activitySubscription$ = null;

    this.visibilitySubscription$?.unsubscribe();
    this.visibilitySubscription$ = null;

    this.warningShown.set(false);

    if (this.notificationRef) {
      this.notificationRef.close();
      this.notificationRef = null;
    }
  }

  isEnabled(): boolean {
    return this.config.sessionTimeMs > 0;
  }

  isMonitoring(): boolean {
    return this.activitySubscription$ !== null;
  }

  getRemainingTime(): number | null {
    if (!this.isMonitoring() || this.sessionStartTime === 0) {
      return null;
    }

    const elapsed = Date.now() - this.sessionStartTime;
    const remaining = this.config.sessionTimeMs - elapsed;

    return Math.max(0, remaining);
  }

  private setupActivityDetection(): void {
    const activityEvents$ = merge(
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'scroll', { capture: true, passive: true }),
      fromEvent(document, 'touchstart', { passive: true }),
      fromEvent(document, 'touchmove', { passive: true }),
      fromEvent(document, 'wheel', { passive: true }),
      fromEvent(window, 'focus')
    );

    this.activitySubscription$ = activityEvents$
      .pipe(
        filter(() => !this.documentHidden()),
        debounceTime(ACTIVITY_DEBOUNCE_MS),
        tap(() => this.activityDetected$.next()),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.warningShown()) {
          this.resetSessionTimer();
        }
      });
  }

  private setupPageVisibilityMonitoring(): void {
    this.visibilitySubscription$ = fromEvent(document, 'visibilitychange')
      .pipe(
        tap(() => {
          const isHidden = document.hidden;
          this.documentHidden.set(isHidden);
          if (!isHidden) {
            this.checkSessionValidity();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private checkSessionValidity(): void {
    if (this.sessionStartTime === 0) {
      return;
    }

    const now = Date.now();
    const elapsedTime = now - this.sessionStartTime;
    const timeUntilExpiry = this.config.sessionTimeMs - elapsedTime;

    if (timeUntilExpiry <= 0) {
      this.handleSessionExpiry();
    } else if (timeUntilExpiry <= this.config.warningTimeMs && !this.warningShown()) {
      this.showWarning(timeUntilExpiry);
    }
  }

  private resetSessionTimer(): void {
    this.sessionStartTime = Date.now();
    this.warningShown.set(false);

    this.sessionTimer$?.unsubscribe();
    this.sessionTimer$ = null;

    this.warningTimer$?.unsubscribe();
    this.warningTimer$ = null;

    if (this.notificationRef) {
      this.notificationRef.close();
      this.notificationRef = null;
    }

    const timeUntilWarning = this.config.sessionTimeMs - this.config.warningTimeMs;

    this.sessionTimer$ = timer(timeUntilWarning)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.documentHidden()) {
          this.showWarning(this.config.warningTimeMs);
        }
      });
  }

  private showWarning(remainingMs: number): void {
    if (this.warningShown()) {
      return;
    }

    this.warningShown.set(true);
    const warningStartTime = Date.now();
    const expiryTime = warningStartTime + remainingMs;

    this.warningMessage.set(this.formatWarningMessage(remainingMs));

    this.notificationRef = this.dialog.open(PhNotificationComponent, {
      data: {
        actions: [
          {
            act: (ref) => {
              ref.close();
              this.resetSessionTimer();
            },
            text: this.translate.instant('sessionPopup.ok'),
          },
        ],
        value: {
          title: this.translate.instant('sessionPopup.title'),
          message: this.warningMessage(),
        },
        type: PhNotificationType.inaudit,
      },
    });

    this.notificationRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        const now = Date.now();
        const timeLeft = Math.max(0, expiryTime - now);
        
        this.warningTimer$?.unsubscribe();
        this.warningTimer$ = null;
        this.notificationRef = null;
        this.warningShown.set(false);
        
        if (timeLeft > 0) {
          this.resetSessionTimer();
        }
      });

    this.warningTimer$ = interval(100)
      .pipe(
        map(() => {
          const now = Date.now();

          return Math.max(0, expiryTime - now);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((timeLeft) => {
        if (timeLeft === 0) {
          setTimeout(() => {
            this.handleSessionExpiry();
          }, 0);

          return;
        }

        const message = this.formatWarningMessage(timeLeft);
        this.warningMessage.set(message);
        
        if (this.notificationRef) {
          this.notificationRef.updateData({ message });
        }
      });
  }

  private formatWarningMessage(timeMs: number): string {
    const seconds = Math.ceil(timeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const timeString = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;

    return `${this.translate.instant('sessionPopup.body')}\n${this.translate.instant(
      'sessionPopup.expiringIn'
    )} ${timeString}`;
  }

  private handleSessionExpiry(): void {
    const expiryCallback = this.onSessionExpiry;
    
    this.stopMonitoring();

    if (this.notificationRef) {
      this.notificationRef.close();
      this.notificationRef = null;
    }

    if (expiryCallback) {
      expiryCallback();
    }
    
    this.onSessionExpiry = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopMonitoring();
    this.onSessionExpiry = null;
  }
}

